/**
 * Banque de médias du document courant — module pur, sans React.
 *
 * Réponse de `media_bank` (concern `MediaBankServing` côté back) :
 * `[{ id, name, company_work_id, media: [<MediaSerializer>] }]`.
 *
 * Un média porte trois familles possibles (`image`, `gif`, `video`) et, pour
 * les images seules, des paliers de résolution. Le sérialiseur ne porte pas le
 * nom du métier, seulement son id : on n'affiche donc pas de niveau « métier »
 * au-dessus des dossiers, exactement comme le picker de l'éditeur classique,
 * qui aplatit déjà les dossiers d'une offre couvrant plusieurs métiers.
 */

export const MEDIA_TYPES = {
  image: 'image',
  gif: 'gif',
  video: 'video',
};

/** Accents et casse ignorés : les noms de fichiers viennent d'utilisateurs. */
export function normalizeForSearch(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function normalizeVariants(raw) {
  if (!raw || typeof raw !== 'object') return {};
  return Object.entries(raw).reduce((acc, [key, variant]) => {
    if (variant?.url) {
      acc[key] = {
        url: variant.url,
        width: variant.width ?? null,
        height: variant.height ?? null,
      };
    }
    return acc;
  }, {});
}

/**
 * Un média de la banque, forme interne.
 *
 * `url` (l'original) n'est plus lu : chaque contexte choisit son palier dans
 * `variants`. Il reste le repli des familles qui n'ont aucun palier — un GIF ou
 * une vidéo n'est jamais redimensionné, le back n'a donc rien à leur générer.
 */
export function normalizeBankMedia(raw) {
  if (!raw) return null;
  const variants = normalizeVariants(raw.variants);
  const source = variants.max?.url || raw.url;
  if (!source) return null;

  const type = raw.type || MEDIA_TYPES.image;
  return {
    id: raw.id,
    filename: raw.filename || '',
    type,
    webOnly: raw.web_only === true,
    posterUrl: raw.poster_url || null,
    width: raw.width ?? null,
    height: raw.height ?? null,
    byteSize: raw.byte_size ?? null,
    variants,
    srcset: typeof raw.srcset === 'string' ? raw.srcset : '',
    /* Repli pour les familles sans palier ; les images passent par `variants`. */
    source,
  };
}

/** Dossiers exploitables : ceux qui contiennent au moins un média affichable. */
export function normalizeMediaBank(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((folder) => ({
      id: folder?.id,
      name: folder?.name || 'Sans nom',
      media: (Array.isArray(folder?.media) ? folder.media : [])
        .map(normalizeBankMedia)
        .filter(Boolean),
    }))
    .filter((folder) => folder.id != null && folder.media.length > 0);
}

/** Vignette d'un média : la plus petite variante disponible, sinon le poster. */
export function mediaThumbnailUrl(media) {
  if (!media) return '';
  if (media.type === MEDIA_TYPES.video) return media.posterUrl || '';
  return media.variants.web_sm?.url || media.variants.web_md?.url || media.source;
}

/**
 * Filtre du picker : recherche texte et famille de médias.
 *
 * Un dossier dont le **nom** correspond garde tous ses médias : on cherche
 * « chantiers » pour voir le dossier, pas pour voir les fichiers dont le nom
 * contiendrait ce mot. Sinon on filtre sur les noms de fichiers. Le filtre par
 * famille, lui, s'applique toujours — c'est un périmètre, pas une recherche.
 */
export function filterMediaBank(folders, query, { types = null } = {}) {
  const needle = normalizeForSearch(query).trim();
  if (!needle && !types) return folders;

  return folders
    .map((folder) => {
      const scoped = types
        ? folder.media.filter((media) => types.includes(media.type))
        : folder.media;
      if (!scoped.length) return null;
      if (!needle) return scoped === folder.media ? folder : { ...folder, media: scoped };
      if (normalizeForSearch(folder.name).includes(needle)) {
        return scoped === folder.media ? folder : { ...folder, media: scoped };
      }
      const media = scoped.filter(
        (item) => normalizeForSearch(item.filename).includes(needle),
      );
      return media.length ? { ...folder, media } : null;
    })
    .filter(Boolean);
}

/** Familles proposées par le filtre du picker, dans l'ordre d'affichage. */
export const MEDIA_TYPE_FILTERS = [
  { value: 'all', label: 'Tous', types: null },
  { value: MEDIA_TYPES.image, label: 'Images', types: [MEDIA_TYPES.image] },
  { value: MEDIA_TYPES.gif, label: 'GIF', types: [MEDIA_TYPES.gif] },
  { value: MEDIA_TYPES.video, label: 'Vidéos', types: [MEDIA_TYPES.video] },
];

/**
 * État affichable d'un picker de banque — commun aux deux éditeurs.
 *
 * Les deux pickers ne partagent pas leur rendu (l'un est en Ant Design, l'autre
 * en Mantine) mais bien leur logique : deux périmètres qui se composent (celui
 * du champ, `types`, et celui que l'utilisateur pose, `family`), une recherche
 * texte, et la liste des filtres qui valent la peine d'être montrés.
 *
 * `filters` est vide quand une seule famille est présente : un filtre sans
 * alternative occupe de la place et laisse croire qu'il manque quelque chose.
 */
export function mediaBankView(folders, { query = '', types = null, family = 'all' } = {}) {
  const scoped = filterMediaBank(Array.isArray(folders) ? folders : [], '', { types });

  const present = new Set(scoped.flatMap((folder) => folder.media.map((media) => media.type)));
  const filters = present.size < 2
    ? []
    : MEDIA_TYPE_FILTERS.filter(
        (filter) => !filter.types || filter.types.some((type) => present.has(type)),
      );

  /* Une famille qui n'est plus présente (dernier GIF supprimé, filtre resté
     sur « GIF ») ne doit pas vider la liste : on retombe sur « Tous ». */
  const active = filters.find((filter) => filter.value === family);

  return {
    scoped,
    filters,
    visible: filterMediaBank(scoped, query, { types: active?.types || null }),
  };
}
